import { Controllers } from "@infoland/iprova-api";
import { ActionTree, GetterTree, MutationTree } from "vuex";
import { RootState } from "..";

export type term_id = string;
export type TermDefinitionReference = {
    [key: string]: term_id; //value is term-id (which is the term to use for definition lookup)
};

type DefinitionIndex = {
    //key = term_id (but typescript does not allow alias, thus using tring
    [key: string]: string;
};

interface State {
    terms: TermDefinitionReference;
    definitions: DefinitionIndex;
}

function initialize(): State {
    return {
        terms: null,
        definitions: {}
    };
}

const getters: GetterTree<State, RootState> = {
    terms(state: State): TermDefinitionReference {
        return state.terms;
    },
    definitions(state: State): DefinitionIndex {
        return state.definitions;
    }
};

const mutations: MutationTree<State> = {
    terms(state: State, payload: TermDefinitionReference) {
        state.terms = payload;
    },
    definition(state: State, { term, definition }: { term: term_id; definition: string }) {
        state.definitions[term] = definition;
    }
};

interface api_glossary_item {
    term: term_id;
    variants: string[];
}

interface api_glossary_item_with_definition extends api_glossary_item {
    definition: string;
}

const actions: ActionTree<State, RootState> = {
    async loadGlossaryAsync({ commit }) {
        const data = await Controllers.GenericController.getAsync<api_glossary_item[]>("glossary_items");
        const terms: TermDefinitionReference = {};

        for (let index = 0; index < data.length; index++) {
            const item = data[index];
            terms[item.term] = item.term;
            for (let variant_index = 0; variant_index < item.variants.length; variant_index++) {
                const variant = item.variants[variant_index];
                terms[variant] = item.term;
            }
        }
        commit("terms", terms);
    },

    async getDefinitionAsync({ state, commit, getters }, term_or_variant: string): Promise<string> {
        if (state.definitions[term_or_variant] !== undefined) {
            return state.definitions[term_or_variant];
        }

        const term = state.terms[term_or_variant];

        //call API
        let item = (
            await Controllers.GenericController.getAsync<api_glossary_item_with_definition[]>(
                `glossary_items?term_equals=${encodeURIComponent(term)}&include_definitions=true`
            )
        )[0];

        //store result for next lookup
        commit("definition", { term: item.term, definition: item?.definition || null }); //assign null when response is empty so it is not called again

        //return definition
        return item?.definition;
    }
};

const state: State = initialize();

// export the namespace
export const namespace = "glossary";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
